import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { gql, useMutation } from '@apollo/client';

interface DeleteDialogProps {
  open: boolean;
  onClose: () => void;
  deviceId: string;
  onDeleted?: () => void;  
}

const DELETE_DEVICE = gql`
  mutation DeleteDevice($deviceId: String!) {
    deleteDevice(device_id: $deviceId)
  }
`;

const DeleteDialog: React.FC<DeleteDialogProps> = ({ open, onClose, deviceId, onDeleted }) => {
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const [deleteDevice, { loading }] = useMutation(DELETE_DEVICE, {
    variables: { deviceId },
    update(cache) {
      cache.evict({ id: `Device:${deviceId}` });
      cache.gc();
    },
    onCompleted: () => {
      setSnackbar({ message: 'Device deleted successfully', severity: 'success' });
      onDeleted?.(); 
      onClose();
    },
    onError: (err) => {
      setSnackbar({ message: err.message, severity: 'error' });
      onClose();
    },
  });

  const handleCloseSnackbar = () => setSnackbar(null);

  const handleDialogClose = () => {
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleDialogClose}>
        <DialogTitle sx={{ pt: '5%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Are you sure you want to delete this device?</span>
          <IconButton aria-label="close" onClick={handleDialogClose} size="small" sx={{ color: 'inherit', right: -15, top: -15 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogActions sx={{ pb: '5%', px: '5%', justifyContent: 'center', gap: 2 }}>
          <Button onClick={handleDialogClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => deleteDevice()}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={snackbar?.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        ContentProps={{
          sx: {
            backgroundColor: snackbar?.severity === 'success' ? 'green' : 'red',
            color: '#fff',
          },
        }}
      />
    </>
  );
};

export default DeleteDialog;